import React, { useState, useMemo, useEffect } from "react";
import Modal from "react-modal";
import "./CategoryModal.css";

const CategoryModal = ({
  isOpen,
  onClose,
  categoryData = [],
  filteredCategoryData = null,
  selectedChildCategory = null,
  expandedParentCategory = null,
  onToggleAccordion,
  onSelectCategory,
  loading = false,
  error = null,
  modalStyles = null
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  const baseData = filteredCategoryData && filteredCategoryData.length > 0 
    ? filteredCategoryData 
    : categoryData;

  const displayData = useMemo(() => {
    if (!searchTerm.trim()) {
      return baseData;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    
    return baseData
      .map((parentCategory) => {
        const parentMatches = parentCategory.parent_category_name
          .toLowerCase()
          .includes(searchLower);

        const matchingChildren = parentCategory.fws_categories.filter(
          (childCategory) =>
            childCategory.category_name.toLowerCase().includes(searchLower)
        );

        if (parentMatches || matchingChildren.length > 0) {
          return {
            ...parentCategory,
            fws_categories: parentMatches 
              ? parentCategory.fws_categories 
              : matchingChildren
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [baseData, searchTerm]);

  // Default modal styles (matching PostJobView style)
  const defaultStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    },
    content: {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      border: 'none',
      background: 'transparent',
      overflow: 'visible',
      borderRadius: '0',
      outline: 'none',
      padding: '0',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '80vh'
    }
  };

  const styles = modalStyles || defaultStyles;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={styles}
      contentLabel="Category Selection Modal"
      scrollable={true}
    >
      <div className="modal-dialog loginpopup" role="document">
        <div className="modal-content category-modal-content-border">
          <div className="category-modal">
            <div className="category-modal-header">
              <h5 className="category-modal-title">Select Category</h5>
              <button 
                type="button" 
                className="category-modal-close" 
                onClick={onClose}
              >
                ×
              </button>
            </div>
            <div className="category-modal-body">
              <div className="category-modal-search-container">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    className="search-input category-modal-search-input"
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              {loading ? (
                <div className="category-modal-loading">
                  <div>Loading categories...</div>
                </div>
              ) : error ? (
                <div className="category-modal-error">
                  <div>{error}</div>
                  Please try login again.
                </div>
              ) : (
                <div className="accordion-container">
                  {displayData.length === 0 ? (
                    <div className="category-modal-no-results">
                      <div>No categories found matching "{searchTerm}"</div>
                    </div>
                  ) : (
                    displayData.map((parentCategory, index) => {
                      const shouldExpand = searchTerm.trim() 
                        ? true 
                        : expandedParentCategory === parentCategory.id;
                      
                      return (
                        <div key={parentCategory.id || index} className="accordion-item">
                          <div 
                            className={`accordion-header ${shouldExpand ? 'expanded' : ''}`}
                            onClick={() => onToggleAccordion(parentCategory.id)}
                          >
                            <span className="accordion-title">{parentCategory.parent_category_name}</span>
                            <span className={`accordion-chevron ${shouldExpand ? 'expanded' : ''}`}>
                              <img 
                                src="/assets/images/dropdownIcon.svg" 
                                alt="dropdown" 
                                className="dropdown-icon"
                              />
                            </span>
                          </div>
                          
                          {shouldExpand && (
                            <div className="accordion-content">
                              <ul className="child-category-list">
                                {parentCategory.fws_categories.length === 0 ? (
                                  <li className="category-modal-child-category-empty">
                                    No matching subcategories
                                  </li>
                                ) : (
                                  parentCategory.fws_categories.map((childCategory, childIndex) => (
                                    <li 
                                      key={childCategory.id || childIndex}
                                      className={`child-category-item ${selectedChildCategory === childCategory.category_name ? 'selected' : ''}`}
                                      onClick={() => onSelectCategory(parentCategory.parent_category_name, childCategory.category_name)}
                                    >
                                      <input 
                                        type="radio" 
                                        name="child-category" 
                                        value={childCategory.category_name}
                                        className="child-category-radio"
                                        checked={selectedChildCategory === childCategory.category_name}
                                        onChange={() => onSelectCategory(parentCategory.parent_category_name, childCategory.category_name)}
                                      />
                                      <label className="child-category-label">{childCategory.category_name}</label>
                                    </li>
                                  ))
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CategoryModal;

